/**
 * @fileoverview Popover wrapper around the shared ViolationPicker from
 * @firesquirrel/local-db. Used by CanvasViolationsPanel to bind a code
 * to a field without leaving the canvas.
 * @module template-builder
 *
 * Responsibilities:
 * - Present the ViolationPicker in a modal-like overlay anchored to the
 *   Violations panel.
 * - Emit a `{ code, title, codeSetId }` selection up to the panel.
 *
 * Dependencies:
 * - @firesquirrel/local-db (ViolationPicker)
 *
 * Architecture:
 * - Receives `dexieDb` from the host app via TemplateCanvas; purely local
 *   Dexie reads.
 *
 * Notes:
 * - When `dexieDb` is absent the component renders a guidance message so
 *   the canvas still works in environments without a wired Dexie.
 */
import { useCallback, useEffect } from 'react';
import { X } from 'lucide-react';
import { ViolationPicker } from '@firesquirrel/local-db';
import type { ViolationPickerSelection } from '@firesquirrel/local-db';

export interface ViolationPickerPopoverProps {
  open: boolean;
  onClose: () => void;
  onSelect: (selection: ViolationPickerSelection) => void;
  /** Optional restricted code set ids (e.g. only the active one). */
  codeSetIds?: string[];
  /** Dexie instance from the host app. Required for real picker usage. */
  dexieDb?: any;
}

/**
 * @component ViolationPickerPopover
 *
 * Purpose:
 * - Show the shared ViolationPicker as a focused overlay.
 *
 * Props:
 * - open, onClose, onSelect, codeSetIds, dexieDb.
 *
 * Behavior:
 * - Keyboard Escape closes the popover.
 * - Picker returns a single selection via `onPickOne`.
 *
 * Data Source:
 * - local (Dexie) via ViolationPicker.
 */
export function ViolationPickerPopover({
  open,
  onClose,
  onSelect,
  codeSetIds,
  dexieDb,
}: ViolationPickerPopoverProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handlePick = useCallback((sel: ViolationPickerSelection) => {
    onSelect(sel);
    onClose();
  }, [onSelect, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-gray-900/30 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Select a violation code"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 rounded-t-lg">
          <span className="text-sm font-semibold text-gray-800">Select violation code</span>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close violation picker"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-hidden p-2">
          {dexieDb ? (
            <ViolationPicker
              db={dexieDb}
              mode="single"
              codeSetIds={codeSetIds}
              onPickOne={handlePick}
              autoFocus
              placeholder="Search violation codes…"
              className="h-full"
            />
          ) : (
            <div className="p-4 text-xs text-gray-500">
              Violation picker is unavailable — the host app has not injected a Dexie instance.
              Configure <code className="font-mono text-[11px]">dexieDb</code> on the canvas to enable picking.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ViolationPickerPopover;
